// Shared decision-draft helpers for conversation surfaces.
import { cssEscape } from "../core/dom-helpers.js";

function ensureDecisionDraft(decisionDrafts, decisionID) {
  if (!decisionDrafts[decisionID]) {
    decisionDrafts[decisionID] = {};
  }
  return decisionDrafts[decisionID];
}

export function decisionDraftValue(decisionDrafts, decisionID, fieldID, fallback) {
  const draft = decisionDrafts?.[decisionID] || {};
  if (Object.prototype.hasOwnProperty.call(draft, fieldID)) {
    return draft[fieldID];
  }
  return fallback;
}

export function clearDecisionDraft(decisionDrafts, decisionID) {
  if (!decisionDrafts) {
    return;
  }
  delete decisionDrafts[decisionID];
}

// Custom freeform answers are stored under the base field id plus a shared "__custom" suffix.
// Renderers and form collection must both use this helper so the convention stays aligned.
export function decisionCustomFieldID(fieldID) {
  return `${String(fieldID || "")}__custom`;
}

export function decisionOptionSelected(currentValue, optionID) {
  if (Array.isArray(currentValue)) {
    return currentValue.includes(optionID);
  }
  return String(currentValue || "") === String(optionID);
}

export function captureDecisionDraftInput(decisionDrafts, target) {
  const form = target.closest("[data-decision-id]");
  if (!form) {
    return;
  }
  const decisionID = form.getAttribute("data-decision-id") || "";
  const fieldID = target.getAttribute("data-decision-field") || target.name || "";
  if (!decisionID || !fieldID) {
    return;
  }
  const draft = ensureDecisionDraft(decisionDrafts, decisionID);
  if (target instanceof HTMLInputElement && target.type === "radio") {
    if (!target.checked) {
      return;
    }
    draft[fieldID] = target.value;
    return;
  }
  if (target instanceof HTMLInputElement && target.type === "checkbox") {
    draft[fieldID] = target.checked;
    return;
  }
  if (target instanceof HTMLSelectElement && target.multiple) {
    draft[fieldID] = [...target.selectedOptions].map((option) => option.value);
    return;
  }
  draft[fieldID] = target.value;
}

export function collectDecisionResponseValues(decisionDrafts, form, decisionItem) {
  const values = {};
  for (const field of decisionItem.fields) {
    const elements = form ? [...form.querySelectorAll(`[data-decision-field="${cssEscape(field.id)}"]`)] : [];
    const customFieldID = decisionCustomFieldID(field.id);
    const customValue = String(decisionDraftValue(decisionDrafts, decisionItem.id, customFieldID, "") || "").trim();
    const draftValue = decisionDraftValue(decisionDrafts, decisionItem.id, field.id, field.multiple ? [] : "");
    const type = String(field.type || "text").toLowerCase();
    if (type === "radio") {
      if (customValue) {
        values[field.id] = customValue;
      } else {
        const selected = elements.find((item) => item instanceof HTMLInputElement && item.checked);
        values[field.id] = selected ? selected.value : draftValue;
      }
      if (field.required && String(values[field.id] || "").trim() === "") {
        throw new Error(`${field.label || field.id} is required`);
      }
      continue;
    }
    if (elements[0] instanceof HTMLSelectElement && elements[0].multiple) {
      if (customValue) {
        values[field.id] = [customValue];
        continue;
      }
      values[field.id] = [...elements[0].selectedOptions].map((option) => option.value);
      continue;
    }
    if (elements[0] instanceof HTMLSelectElement) {
      values[field.id] = customValue || elements[0].value;
      if (field.required && values[field.id] === "") {
        throw new Error(`${field.label || field.id} is required`);
      }
      continue;
    }
    if (elements[0] instanceof HTMLInputElement && elements[0].type === "checkbox") {
      values[field.id] = elements[0].checked;
      continue;
    }
    const source = elements[0];
    values[field.id] = source && "value" in source ? source.value : decisionDraftValue(decisionDrafts, decisionItem.id, field.id, "");
    if (field.required && (values[field.id] === "" || values[field.id] == null || (Array.isArray(values[field.id]) && values[field.id].length === 0))) {
      throw new Error(`${field.label || field.id} is required`);
    }
  }
  return values;
}
